import { useState } from "react";
import Button from "./Button";
import Loading from "./Loading";
import AlertDenger from "./AlertDenger";

// Collects the receiver's full name, national ID and phone number before a
// student is transferred to MOHE Validation. The backend validates the same rules.
export const PHONE_REGEX = /^\+?\d{7,15}$/;
// Egyptian national ID (14 digits) or a passport number.
export const NATIONAL_ID_REGEX = /^[A-Z0-9]{5,20}$/;

export const normalizeNationalId = (value) => value.replace(/\s/g, "").toUpperCase();

export const validateReceiver = (name, nationalId, phone) => {
  if (!name.trim()) return "Please enter the receiver's full name";
  if (!NATIONAL_ID_REGEX.test(normalizeNationalId(nationalId)))
    return "Please enter a valid National ID (letters and digits, 5-20 characters)";
  if (!PHONE_REGEX.test(phone.replace(/[\s-]/g, "")))
    return "Please enter a valid phone number (7-15 digits)";
  return "";
};

export default function MoheReceiverModal({
  onClose,
  onSubmit,
  loading = false,
  error = "",
  count = 1,
  // Pre-fill when editing an existing receiver.
  initial = {},
  submitLabel = "Transfer to MOHE Validation",
}) {
  const [name, setName] = useState(initial.receiver_name || "");
  const [nationalId, setNationalId] = useState(initial.receiver_national_id || "");
  const [phone, setPhone] = useState(initial.receiver_phone || "");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = validateReceiver(name, nationalId, phone);
    if (message) {
      setLocalError(message);
      return;
    }
    setLocalError("");
    onSubmit({
      receiver_name: name.trim(),
      receiver_national_id: normalizeNationalId(nationalId),
      receiver_phone: phone.replace(/[\s-]/g, ""),
    });
  };

  const shownError = localError || error;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <form
        className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 p-6"
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Certificate - Receiver</h2>
          <span
            className="cursor-pointer rounded-full w-8 h-8 bg-red-400 text-white
              flex items-center justify-center active:scale-90 hover:bg-red-500 text-sm"
            onClick={() => !loading && onClose()}
          >
            X
          </span>
        </div>

        {count > 1 && (
          <p className="text-sm text-gray-600 mb-3">
            This receiver will be saved for all {count} selected students.
          </p>
        )}

        {shownError && (
          <div className="mb-3">
            <AlertDenger text={shownError} />
          </div>
        )}

        <label className="font-semibold mb-1 block">
          Receiver Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          maxLength={255}
          autoFocus
          placeholder="Full name"
          className="bg-gray-100 rounded-lg p-2 outline-blue-700 border w-full mb-4"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="font-semibold mb-1 block">
          Receiver National ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          maxLength={20}
          placeholder="National ID or passport number"
          className="bg-gray-100 rounded-lg p-2 outline-blue-700 border w-full mb-4"
          value={nationalId}
          onChange={(e) => setNationalId(e.target.value)}
        />

        <label className="font-semibold mb-1 block">
          Receiver Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          maxLength={20}
          placeholder="e.g. 01012345678"
          className="bg-gray-100 rounded-lg p-2 outline-blue-700 border w-full mb-4"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />

        <Button
          type="submit"
          text={!loading ? submitLabel : <Loading />}
          color="bg-green-500 disabled:bg-gray-300 w-full"
          disabled={loading}
        />
      </form>
    </div>
  );
}
